export enum BookingSource {
  DIRECT = 'direct',
  AIRBNB = 'airbnb',
  BOOKING_COM = 'booking_com',
  EXPEDIA = 'expedia',
  AXIS_ROOMS = 'axis_rooms',
  MANUAL = 'manual',
}

export enum BookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CHECKED_IN = 'checked_in',
  CHECKED_OUT = 'checked_out',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
}

/**
 * How a booking is (or will be) paid for. Orthogonal to BookingSource — an OTA
 * booking may be prepaid to the OTA or payable at the hotel. `null` on a Booking
 * means "not set" (the common case today: AxisRooms API-9 sends no payment
 * field, and internal/manual bookings default to unset until staff pick one).
 */
export enum PaymentType {
  /** Guest prepaid the OTA; the OTA collects and settles (OTA-collect). */
  PREPAID_OTA = 'prepaid_ota',
  /** Guest pays the hotel directly at/after check-in (hotel-collect). */
  PAY_AT_HOTEL = 'pay_at_hotel',
  /** Charges billed to a company account (corporate / bill-to-company). */
  BILL_TO_COMPANY = 'bill_to_company',
}

export interface Booking {
  id: string;
  unitId: string;
  guestId: string | null;
  source: BookingSource;
  externalBookingId: string | null;
  status: BookingStatus;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  baseAmount: string;
  totalAmount: string;
  currency: string;
  revenueShareAmount: string | null;
  cancellationReason: string | null;
  specialRequests: string | null;
  /** Payment type (prepaid via OTA / pay at hotel / bill to company); null = not set. */
  paymentType: PaymentType | null;
  createdAt: string;
  updatedAt: string;
}

/** Maps each unitId to the list of bookings occupying that unit. */
export type CalendarResponse = Record<string, Booking[]>;

export enum GroupBookingStatus {
  TENTATIVE = 'tentative',
  CONFIRMED = 'confirmed',
  CHECKED_IN = 'checked_in',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface GroupBooking {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  companyId: string | null;
  contactGuestId: string | null;
  checkIn: string;
  checkOut: string;
  status: GroupBookingStatus;
  totalUnits: number;
  totalAmount: string;
  currency: string;
  notes: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  bookings?: Booking[];
  company?: { id: string; name: string } | null;
  contactGuest?: { id: string; fullName: string; email: string | null } | null;
}
